import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTwitter, faYoutube, faInstagram, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { AnalyticsService } from './services/analytics.service';
import { CuentaPreferenciasService } from './services/cuenta-preferencias.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    MatGridListModule,
    RouterOutlet,
    RouterLink,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDialogModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'agromet-web';
  private readonly onScroll = this.onWindowScroll.bind(this);
  private headerResizeObserver?: ResizeObserver;

  constructor(library: FaIconLibrary, public dialog: MatDialog, private analytics: AnalyticsService,
    cuentaPreferencias: CuentaPreferenciasService) {
    library.addIcons(faTwitter, faYoutube, faInstagram, faFacebook);

    // Favoritas de la cuenta al iniciar sesión (y las del visitante al cerrarla)
    cuentaPreferencias.iniciar();

    // Estadísticas: qué herramientas (diálogos) se abren. Se identifica cada una por su
    // título visible, que no cambia con la compilación (los nombres de clase sí).
    this.dialog.afterOpened.subscribe(ref => {
      setTimeout(() => {
        const titulo = document.getElementById(ref.id)
          ?.querySelector('h2, h1, .titulo')?.textContent?.replace(/\s+/g, ' ').trim();
        this.analytics.registrar('abrir_herramienta', { herramienta: (titulo || 'sin título').slice(0, 100) });
      }, 400);
    });
  }

  ngOnInit(): void {
    // Con `html, body { height: 100%; overflow-x: hidden }` el que se desplaza es <body>, no la ventana,
    // y su evento scroll no burbujea: se escucha en fase de captura para recibir ambos casos.
    window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    this.observeHeaderHeight();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll, { capture: true });
    this.headerResizeObserver?.disconnect();
  }

  onWindowScroll(event?: Event): void {
    // Ignora el desplazamiento de contenedores internos (tablas, paneles con overflow).
    const target = event?.target;
    if (target && target !== document && target !== document.body && target !== document.documentElement) {
      return;
    }
    const scrollTop = Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop);
    const header = document.querySelector('.header-container');
    if (!header) {
      return;
    }
    // Histéresis: al achicarse el header cambia el alto de la página; umbrales distintos evitan que parpadee.
    const isScrolled = header.classList.contains('scrolled') ? scrollTop > 10 : scrollTop > 60;
    header.classList.toggle('scrolled', isScrolled);
  }

  /**
   * El header cambia de alto según el contenido (badge de alerta presente/ausente,
   * si el menú mobile envuelve a dos líneas, el efecto "shrink" al hacer scroll, etc.).
   * En vez de adivinar un margin-top fijo para .main-content, medimos el alto real
   * del header y lo exponemos como variable CSS para que siempre calce.
   */
  private observeHeaderHeight(): void {
    const header = document.querySelector<HTMLElement>('.header-container');
    if (!header || typeof ResizeObserver === 'undefined') {
      return;
    }

    const syncHeight = () => {
      // Sólo con el header completo: si se midiera la versión achicada, el contenido saltaría hacia arriba.
      if (header.classList.contains('scrolled')) {
        return;
      }
      document.documentElement.style.setProperty('--header-h', `${header.getBoundingClientRect().height}px`);
    };

    this.headerResizeObserver = new ResizeObserver(syncHeight);
    this.headerResizeObserver.observe(header);
    syncHeight();
  }

  
  async openDiseaseDebugDialog(): Promise<void> {
    const { DiseaseConditionsDialogComponent } = await import('./disease-conditions-dialog/disease-conditions-dialog.component');

    this.dialog.open(DiseaseConditionsDialogComponent, {
      width: 'min(1120px, 95vw)',
      maxWidth: '95vw',
      height: '92vh',
      maxHeight: '92vh',
      panelClass: 'do-dialog',
      autoFocus: false,
      restoreFocus: false
    });
  }
  async openDialog(): Promise<void> {
    const { AuthComponent } = await import('./auth/auth.component');

    this.dialog.open(AuthComponent, {
      width: 'min(460px, 94vw)',
      maxWidth: '94vw',
      panelClass: 'auth-dialog',
      autoFocus: false,
      restoreFocus: false,
      data: {
        info: 'Ingreso a usuarios'
      }
    });
  }
}
